import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Settings2, IndianRupee, SlidersHorizontal } from "lucide-react";

const fmtMoney = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

const DELIVERY_STATUSES = ["Pending", "In Transit", "Delivered", "Undelivered", "Cancelled"];

// Inline per-order delivery charge editor — saves on blur / Enter.
function DeliveryChargeInput({ row, onSaved }) {
  const current = row.profit_breakdown?.delivery_charge ?? "";
  const [val, setVal] = useState(current);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(current); }, [current]);
  const save = async () => {
    if (String(val) === String(current)) return;
    setSaving(true);
    try {
      await api.put(`/orders/${row.id}/profit-overrides`, { delivery_charge: val === "" ? null : Number(val) });
      toast.success("Delivery charge saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Input
      type="number" step="0.01" min="0"
      className="w-20 h-8 text-xs px-2"
      value={val}
      disabled={saving}
      placeholder="0"
      onChange={(e) => setVal(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      data-testid={`profit-delivery-${row.order_id}`}
    />
  );
}

export default function Profits() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [settings, setSettings] = useState(null);
  const [filters, setFilters] = useState({ start: "", end: "", source: "all", box: "all", agent: "all", delivery: "all" });
  const [agents, setAgents] = useState([]);
  const [openSettings, setOpenSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ customer_acquisition_cost: 0, warehouse_charge: 0, easycom_charge: 0, box_costs: {} });
  // Per-order override dialog
  const [ovrRow, setOvrRow] = useState(null);
  const [ovrForm, setOvrForm] = useState({});
  const PAGE = 50;

  const load = useCallback(async (nextOffset = 0) => {
    const params = { limit: PAGE, offset: nextOffset };
    if (filters.start) params.start_date = filters.start;
    if (filters.end) params.end_date = filters.end;
    if (filters.source !== "all") params.order_source = filters.source;
    if (filters.box !== "all") params.box_type = filters.box;
    if (filters.agent !== "all") params.agent_id = filters.agent;
    if (filters.delivery !== "all") params.delivery_status = filters.delivery;
    try {
      const { data } = await api.get("/profits", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setOffset(data.offset || 0);
      setSettings(data.settings);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [filters]);

  useEffect(() => { load(0); }, [load]);
  useEffect(() => {
    api.get("/users/agents").then(({ data }) => setAgents(data || [])).catch(() => {});
  }, []);

  const openSettingsDialog = () => {
    if (!settings) return;
    setSettingsForm({
      customer_acquisition_cost: settings.customer_acquisition_cost || 0,
      warehouse_charge: settings.warehouse_charge || 0,
      easycom_charge: settings.easycom_charge || 0,
      box_costs: { ...(settings.box_costs || {}) },
    });
    setOpenSettings(true);
  };

  const saveSettings = async () => {
    try {
      await api.put("/settings/profit", {
        customer_acquisition_cost: Number(settingsForm.customer_acquisition_cost || 0),
        warehouse_charge: Number(settingsForm.warehouse_charge || 0),
        easycom_charge: Number(settingsForm.easycom_charge || 0),
        box_costs: Object.fromEntries(
          Object.entries(settingsForm.box_costs || {}).map(([k, v]) => [k, Number(v || 0)]),
        ),
      });
      toast.success("Profit settings saved");
      setOpenSettings(false);
      load(offset);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setBox = async (orderId, box) => {
    try {
      await api.put(`/orders/${orderId}/box-type`, { box_type: box });
      toast.success(`Box set to ${box}`);
      load(offset);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Open per-order override editor. Prefills from stored overrides;
  // blank field means "fall back to settings default".
  const openOverride = (row) => {
    setOvrRow(row);
    setOvrForm({
      warehouse: row.warehouse_charge_override ?? "",
      easycom: row.easycom_charge_override ?? "",
      cac: row.customer_acquisition_cost_override ?? "",
      delivery: row.delivery_charge ?? "",
      box_type: row.box_type || "",
    });
  };
  const saveOverride = async () => {
    // "" (empty) → clear override (null). Non-empty numeric → set.
    const body = {};
    const num = (v) => v === "" || v === null || v === undefined ? null : Number(v);
    body.warehouse_charge_override = num(ovrForm.warehouse);
    body.easycom_charge_override = num(ovrForm.easycom);
    body.customer_acquisition_cost_override = num(ovrForm.cac);
    body.delivery_charge = num(ovrForm.delivery);
    body.box_type = ovrForm.box_type || null;
    try {
      await api.put(`/orders/${ovrRow.id}/profit-overrides`, body);
      toast.success("Order profit values updated");
      setOvrRow(null);
      load(offset);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = (fmt = "xlsx") => {
    const params = new URLSearchParams({ fmt });
    if (filters.start) params.set("start_date", filters.start);
    if (filters.end) params.set("end_date", filters.end);
    if (filters.source !== "all") params.set("order_source", filters.source);
    if (filters.box !== "all") params.set("box_type", filters.box);
    if (filters.agent !== "all") params.set("agent_id", filters.agent);
    if (filters.delivery !== "all") params.set("delivery_status", filters.delivery);
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/profits/export?${params.toString()}`, { credentials: "include" })
      .then((r) => r.ok ? r.blob() : Promise.reject(`HTTP ${r.status}`))
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `profits_report.${fmt}`;
        a.click();
      })
      .catch((e) => toast.error(`Download failed: ${e}`));
  };

  const boxKeys = useMemo(() => Object.keys(settings?.box_costs || {}).sort(), [settings]);
  const grandProfit = useMemo(() => rows.reduce((s, r) => s + Number(r.profit_breakdown?.profit || 0), 0), [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">Profit Calculator</h1>
          <p className="text-sm text-muted-foreground">Per-order profit — assign a box, review DP + fees, and download the sheet.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={openSettingsDialog} data-testid="profit-open-settings">
              <Settings2 className="w-4 h-4 mr-1" /> Profit Settings
            </Button>
          )}
          <Button onClick={() => download("xlsx")} data-testid="profit-download-btn">
            <Download className="w-4 h-4 mr-1" /> Download Excel
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} data-testid="profit-start-date" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} data-testid="profit-end-date" />
          </div>
          <div>
            <Label className="text-xs">BDM</Label>
            <Select value={filters.agent} onValueChange={(v) => setFilters({ ...filters, agent: v })}>
              <SelectTrigger data-testid="profit-agent-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All BDMs</SelectItem>
                {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Order source</Label>
            <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
              <SelectTrigger data-testid="profit-source-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="Manual Order">Manual Orders</SelectItem>
                <SelectItem value="Claimed Order">Claimed Orders</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Delivery status</Label>
            <Select value={filters.delivery} onValueChange={(v) => setFilters({ ...filters, delivery: v })}>
              <SelectTrigger data-testid="profit-delivery-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {DELIVERY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Box type</Label>
            <Select value={filters.box} onValueChange={(v) => setFilters({ ...filters, box: v })}>
              <SelectTrigger data-testid="profit-box-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All boxes</SelectItem>
                {boxKeys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Card className="w-full px-3 py-2 flex items-center gap-2 bg-emerald-50 border-emerald-200">
              <IndianRupee className="w-4 h-4 text-emerald-700" />
              <div>
                <div className="text-[10px] text-emerald-700 uppercase">Profit (this page)</div>
                <div className="text-sm font-semibold text-emerald-900" data-testid="profit-page-total">{fmtMoney(grandProfit)}</div>
              </div>
            </Card>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>DP</TableHead>
                <TableHead>Box</TableHead>
                <TableHead>Box Cost</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead>EasyCom</TableHead>
                <TableHead>Delivery ₹</TableHead>
                <TableHead>CAC</TableHead>
                <TableHead className="bg-emerald-50">Profit</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={14} className="text-center text-muted-foreground py-8">No orders match your filters</TableCell></TableRow>
              )}
              {rows.map((r) => {
                const b = r.profit_breakdown || {};
                const negative = Number(b.profit) < 0;
                const undelivered = !b.delivered;
                return (
                  <TableRow key={r.id} data-testid={`profit-row-${r.order_id}`} className={undelivered ? "bg-red-50/40" : ""}>
                    <TableCell className="font-mono text-xs">{r.order_id}</TableCell>
                    <TableCell className="text-xs">{r.customer_name}</TableCell>
                    <TableCell className="text-xs">
                      <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium ${b.delivered ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                        {b.delivery_status || "Pending"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{r.order_type_tag_name || "—"} {b.cac_applies ? <span className="text-[10px] text-amber-700">(Fresh)</span> : null}</TableCell>
                    <TableCell className="font-medium">
                      {fmtMoney(b.total_order_amount)}
                      {undelivered && <div className="text-[10px] text-red-700">Revenue = 0 (not delivered)</div>}
                    </TableCell>
                    <TableCell>{fmtMoney(b.dp_total)}</TableCell>
                    <TableCell>
                      <Select value={b.box_type || "__none__"} onValueChange={(v) => v !== "__none__" && setBox(r.id, v)}>
                        <SelectTrigger className="w-24 h-8" data-testid={`profit-box-${r.order_id}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__" disabled>Pick box</SelectItem>
                          {boxKeys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>{fmtMoney(b.box_cost)}</TableCell>
                    <TableCell>{fmtMoney(b.warehouse_charge)}</TableCell>
                    <TableCell>{fmtMoney(b.easycom_charge)}</TableCell>
                    <TableCell>
                      <DeliveryChargeInput row={r} onSaved={() => load(offset)} />
                    </TableCell>
                    <TableCell>{fmtMoney(b.customer_acquisition_cost)}</TableCell>
                    <TableCell className={`font-semibold bg-emerald-50 ${negative ? "text-red-700 bg-red-50" : "text-emerald-800"}`} data-testid={`profit-value-${r.order_id}`}>
                      {fmtMoney(b.profit)}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openOverride(r)} data-testid={`profit-edit-${r.order_id}`} title="Edit per-order values">
                        <SlidersHorizontal className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between p-3 border-t bg-muted/20 text-xs">
          <div>Showing {rows.length ? offset + 1 : 0}–{offset + rows.length} of {total}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => load(Math.max(0, offset - PAGE))} data-testid="profit-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={offset + rows.length >= total} onClick={() => load(offset + PAGE)} data-testid="profit-next">Next</Button>
          </div>
        </div>
      </Card>

      {/* Admin — profit settings */}
      <Dialog open={openSettings} onOpenChange={setOpenSettings}>
        <DialogContent className="max-w-lg" data-testid="profit-settings-dialog">
          <DialogHeader><DialogTitle>Profit Settings</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Customer Acquisition (Fresh only)</Label>
                <Input type="number" step="0.01" value={settingsForm.customer_acquisition_cost}
                  onChange={(e) => setSettingsForm({ ...settingsForm, customer_acquisition_cost: e.target.value })}
                  data-testid="ps-cac" />
              </div>
              <div>
                <Label className="text-xs">Warehouse Charge</Label>
                <Input type="number" step="0.01" value={settingsForm.warehouse_charge}
                  onChange={(e) => setSettingsForm({ ...settingsForm, warehouse_charge: e.target.value })}
                  data-testid="ps-warehouse" />
              </div>
              <div>
                <Label className="text-xs">EasyCom Charge</Label>
                <Input type="number" step="0.01" value={settingsForm.easycom_charge}
                  onChange={(e) => setSettingsForm({ ...settingsForm, easycom_charge: e.target.value })}
                  data-testid="ps-easycom" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Box Costs</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {Object.entries(settingsForm.box_costs).map(([k, v]) => (
                  <div key={k}>
                    <Label className="text-[10px] text-muted-foreground">{k}</Label>
                    <Input type="number" step="0.01" value={v}
                      onChange={(e) => setSettingsForm({ ...settingsForm, box_costs: { ...settingsForm.box_costs, [k]: e.target.value } })}
                      data-testid={`ps-box-${k}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenSettings(false)}>Cancel</Button>
            <Button onClick={saveSettings} data-testid="ps-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-order override dialog — profit_calc & admin can tweak everything for one order */}
      <Dialog open={!!ovrRow} onOpenChange={(o) => { if (!o) setOvrRow(null); }}>
        <DialogContent className="max-w-lg" data-testid="profit-overrides-dialog">
          {ovrRow && (
            <>
              <DialogHeader>
                <DialogTitle>Edit profit values · {ovrRow.order_id}</DialogTitle>
              </DialogHeader>
              <div className="text-xs text-muted-foreground mb-2">
                Leave a field blank to fall back to the global Profit Setting.
                Delivery cost is per-order — no global default.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Warehouse (₹)</Label>
                  <Input type="number" step="0.01" value={ovrForm.warehouse}
                    placeholder={`Default: ${settings?.warehouse_charge ?? 0}`}
                    onChange={(e) => setOvrForm({ ...ovrForm, warehouse: e.target.value })}
                    data-testid="ovr-warehouse" />
                </div>
                <div>
                  <Label className="text-xs">EasyCom (₹)</Label>
                  <Input type="number" step="0.01" value={ovrForm.easycom}
                    placeholder={`Default: ${settings?.easycom_charge ?? 0}`}
                    onChange={(e) => setOvrForm({ ...ovrForm, easycom: e.target.value })}
                    data-testid="ovr-easycom" />
                </div>
                <div>
                  <Label className="text-xs">Delivery (₹)</Label>
                  <Input type="number" step="0.01" value={ovrForm.delivery}
                    placeholder="Per-order"
                    onChange={(e) => setOvrForm({ ...ovrForm, delivery: e.target.value })}
                    data-testid="ovr-delivery" />
                </div>
                <div>
                  <Label className="text-xs">Customer Acquisition (₹)</Label>
                  <Input type="number" step="0.01" value={ovrForm.cac}
                    placeholder={`Default: ${settings?.customer_acquisition_cost ?? 0} (fresh only)`}
                    onChange={(e) => setOvrForm({ ...ovrForm, cac: e.target.value })}
                    data-testid="ovr-cac" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Box</Label>
                  <Select value={ovrForm.box_type || "__none__"} onValueChange={(v) => setOvrForm({ ...ovrForm, box_type: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="ovr-box"><SelectValue placeholder="Pick box" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Clear</SelectItem>
                      {boxKeys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOvrRow(null)}>Cancel</Button>
                <Button onClick={saveOverride} data-testid="ovr-save">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
